import { z } from 'zod';
import { type PermissionCode } from './permissions.js';

/**
 * @aiPurpose enumerate every page-level resource the application surfaces.
 * @aiInvariant cardinality is 34; mirrors the navigation slice of
 *   `PermissionCode` plus the `callCenterLeads` capability resource.
 *   Append-only — removing a value here also removes it from every
 *   Permission row referencing the resource. #698 dropped the obsolete
 *   `'shipments'` resource in favour of `'cargo'` and added the
 *   `'russiaRequests'` and `'warehousesChina'` namespaces. #995 added the
 *   request-level `'roads'` and `'packaging'` dictionaries. #1166 added
 *   the RU-internal `'russiaCarriers'` directory. #1194 added the
 *   `'commercialProposals'` ("КП") aggregate. #1256 added the
 *   `'callCenterLeads'` capability resource (groups the
 *   `callCenterLeads.submit` permission in the grid + i18n tree). #1587 added
 *   the `'dashboard'` resource (groups the leadership `dashboard.read`
 *   permission). #2007 added the `'agent'` resource (groups the
 *   `agent.admin` permission).
 * @aiSecurity none
 */
export const RESOURCES = [
  'requests',
  'deals',
  'cargo',
  'calls',
  'chat',
  'tasks',
  'clients',
  'companies',
  'leads',
  'paymentClaims',
  'payments',
  'accounts',
  'stages',
  'sources',
  'warehouses',
  'priceLists',
  'carriers',
  'users',
  'departments',
  'roles',
  'audit',
  'transportTypes',
  'arrivalCities',
  'roads',
  'packaging',
  'russiaCarriers',
  'russiaRequests',
  'warehousesChina',
  'commercialProposals',
  'logisticsCalculations',
  'cargoProblems',
  'callCenterLeads',
  'dashboard',
  'agent',
] as const satisfies readonly string[];

/**
 * @aiPurpose union of every navigable resource.
 * @aiInvariant equals the runtime members of `RESOURCES`.
 */
export type Resource = (typeof RESOURCES)[number];

/**
 * @aiPurpose UUID-branded identifier of a `Role` row.
 * @aiInvariant runtime is `string`; brand exists only at the type level so
 *   role ids and free-form strings cannot be mixed at API boundaries.
 * @aiSecurity authorisation pivots on this id; do not coerce raw strings
 *   into `RoleId` outside `RoleIdSchema.parse(...)`.
 */
export const RoleIdSchema = z.string().uuid().brand<'RoleId'>();

/**
 * @aiPurpose branded UUID identifying a Role row.
 * @aiInvariant constructed only via `RoleIdSchema.parse`.
 * @aiSecurity see `RoleIdSchema`.
 */
export type RoleId = z.infer<typeof RoleIdSchema>;

/**
 * @aiPurpose lightweight projection of a Role row used by API responses
 *   and the web auth store.
 * @aiInvariant `id` is a branded UUID; `name` is the machine identifier
 *   (`'admin'` | `'manager'` | `'logist_cn'` | `'logist_ru'` for system
 *   roles).
 * @aiSecurity ADMIN role is `name === 'admin' && isSystem === true`;
 *   guards short-circuit on this combination.
 */
export const RoleRefSchema = z
  .object({
    id: RoleIdSchema,
    name: z.string().min(1),
    displayName: z.string().min(1),
    isSystem: z.boolean(),
  })
  .strict();

/**
 * @aiPurpose Role projection for API consumers.
 * @aiInvariant `RoleRefSchema`-validated; never carries permission codes
 *   (those travel separately via `LoginResponse.user.permissions`).
 * @aiSecurity see `RoleRefSchema`.
 */
export type RoleRef = z.infer<typeof RoleRefSchema>;

/**
 * @aiPurpose decide whether a permission set authorises a given code.
 * @aiInvariant `'ALL'` short-circuits; arrays use exact membership.
 * @aiSecurity load-bearing authorisation predicate consumed by every
 *   `PermissionsGuard.canActivate` call and the web `RequirePermission`
 *   route gate.
 */
export function hasPermission(
  perms: ReadonlyArray<PermissionCode> | 'ALL',
  code: PermissionCode,
): boolean {
  if (perms === 'ALL') return true;
  return perms.includes(code);
}

export { PERMISSION_CATALOG, PermissionCodeSchema, type PermissionCode } from './permissions.js';
export { LOGIST_SHIFT_ROLE_NAMES, isShiftRoleName, type LogistShiftRoleName } from './shift.js';
